/**
 * HUPALUPA Grubu — Faturalandırma
 *
 * Patron paneliyle ({@link ./HupalupaGroup.jsx}) AYNI uçtan beslenir:
 * `GET /api/group/hupalupa/monthly`. Orada marka × mecra × ay harcaması zaten
 * canlı geliyor (`brands[].months[ay].platforms[MECRA].spend`); bu sayfa ona
 * fatura yüklerini bindirir. Yeni bir backend rotası YOK, yazılmasın —
 * harcamayı ikinci bir yerden çekmek iki sayfayı ayrı rakamlara düşürür.
 *
 * ── Fatura merdiveni (Efe'nin Excel'inden birebir, Temmuz 2026 ile doğrulandı)
 *   harcama            130.192
 *   × (1 + mecra ücreti)  → 136.051   Google %4,5 · Meta %5 · TikTok yok
 *   ÷ (1 − stopaj)        → 160.060   🔴 BRÜTLEŞTİRME, düz çarpım DEĞİL
 *   × (1 + KDV)           → 192.071
 *   Glowy hakediş %10     →  13.019   🔴 HAM harcama üzerinden, yüklerden önce
 *
 * 🔴🔴 Mecra ücreti stopaj matrahının İÇİNDE. Excel'de Google'ın %4,5'i
 * stopajdan önce biniyor (136.051 → 160.060); Meta'nın %5'i de aynı yere
 * konuldu ("google %4.5 diyor ya" — Efe, 1 Eyl 2026). Ücreti stopajdan sonra
 * eklersen Google sütunu Excel'le tutmaz.
 *
 * 🔴 Meta %5 konum ücreti **Temmuz 2026'dan itibaren** var (`META_FEE_BASLANGIC`).
 * Haziran ve öncesinde hücre "—" kalır; 0,00 yazmak "hesaplandı, sıfır çıktı"
 * demek olurdu. /faturalama sayfasında da aynı tarih kuralı var.
 *
 * ⚠️ Mecra API'lerinin `spend` alanı bu yüklerin HİÇBİRİNİ içermez (ne Google'ın
 * %4,5'i ne Meta'nın %5'i faturaya API'den gelir) — hepsi burada elle bindirilir.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, Loader2, AlertTriangle, Printer, FileSpreadsheet, Info, ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx-js-style';

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const AY_ADLARI = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];
const AY_KISA = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

/** Kaç ay geriye bakılabilsin — fatura kontrolü geçmişe dönük yapılıyor. */
const AY_SAYISI = 12;

/* ── Mecralar ──────────────────────────────────────────────────────────
   Anahtarlar backend'in `platforms` anahtarlarıyla aynı olmak ZORUNDA
   (groupPanel.js: META_ADS / GOOGLE_ADS / TIKTOK_ADS). Logolar /faturalama
   sayfasıyla ortak dosyalar. */
const MECRALAR = [
  { key: 'GOOGLE_ADS', label: 'Google Ads', logo: '/google_ads-512.webp', ucret: 'google' },
  { key: 'META_ADS', label: 'Meta', logo: '/6033716.png', ucret: 'meta' },
  { key: 'TIKTOK_ADS', label: 'TikTok', logo: '/tik-tokiconss.png', ucret: null },
];

/**
 * Varsayılan oranlar. Ekrandan değiştirilebilir ama KAYDEDİLMEZ — bu sayfa
 * kontrol ekranı, oran deposu değil. Kalıcı oran gerekiyorsa
 * /harcama-faturasi'nın `billing_fee_settings` tablosu var.
 */
const VARSAYILAN_ORANLAR = {
  google: 4.5,   // Türkiye dijital hizmet vergisi — Google faturasında tutarın içinde
  meta: 5,       // konum ücreti — Temmuz 2026'dan itibaren
  stopaj: 15,    // brütleştirilir: tutar / (1 − oran) − tutar
  kdv: 20,
  hakedis: 10,   // Glowy hakedişi, HAM harcama üzerinden
};

/** Meta konum ücretinin ilk uygulandığı ay. Düz metin `YYYY-MM` karşılaştırması. */
const META_FEE_BASLANGIC = '2026-07';

/* ── 🔶 GEÇİCİ simülasyon bloğu ────────────────────────────────────────
   Efe, 2 Eyl 2026: "Keramet Ilıcası özelinde bu ay %50 daha az fatura
   keseceğiz" → sayfanın en altında, harcama yarıya inmiş gibi ikinci bir
   fatura merdiveni. 🔴🔴 Yukarıdaki tabloların, özet kartların ve Excel
   çıktısının HİÇBİRİNE dokunmaz; canlı harcama olduğu gibi durur.
   Bu bir SİMÜLASYONDUR — geçici, iş bitince bu blok ve `SIM_*` sabitleri
   silinecek. */
const SIM_MARKA_DESENI = /keramet/i;
const SIM_VARSAYILAN_INDIRIM = 50;

/* ── Biçimleyiciler ────────────────────────────────────────────────── */

const sayi = (n, basamak = 2) =>
  n == null || !Number.isFinite(n)
    ? '—'
    : new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: basamak,
        maximumFractionDigits: basamak,
      }).format(n);

const para = (n, basamak = 2) => (n == null || !Number.isFinite(n) ? '—' : `₺${sayi(n, basamak)}`);

function ayEtiketi(key, kisa = false) {
  const [y, m] = String(key || '').split('-');
  const i = parseInt(m, 10) - 1;
  if (Number.isNaN(i) || !AY_ADLARI[i]) return key || '—';
  return kisa ? `${AY_KISA[i]} ${String(y).slice(2)}` : `${AY_ADLARI[i]} ${y}`;
}

/* ── Hesap ─────────────────────────────────────────────────────────── */

/** O ay o mecraya binen ücret oranı (kesir). Meta'da tarih kuralı işler. */
function ucretOrani(mecra, ay, oranlar) {
  if (mecra.ucret === 'google') return oranlar.google / 100;
  if (mecra.ucret === 'meta') return ay >= META_FEE_BASLANGIC ? oranlar.meta / 100 : 0;
  return 0;
}

/**
 * Tek mecra satırının fatura merdiveni.
 * `ucretYok: true` → o ay o mecrada ücret TANIMSIZ (Meta, Haziran ve öncesi):
 * sütunda "—" yazılır, 0,00 değil.
 */
function satirHesabi(harcama, mecra, ay, oranlar) {
  const oran = ucretOrani(mecra, ay, oranlar);
  const ucretTanimli = mecra.ucret === 'google' || (mecra.ucret === 'meta' && ay >= META_FEE_BASLANGIC);

  const ucret = harcama * oran;
  const ucretli = harcama + ucret;

  // 🔴 Stopaj brütleştirme: net tutarı brüte çıkarır. ×1,15 yazmak kalem
  // başına ~%2,65 eksik verir.
  const s = Math.min(0.99, oranlar.stopaj / 100);
  const stopaj = s > 0 ? ucretli / (1 - s) - ucretli : 0;
  const stopajli = ucretli + stopaj;

  const kdv = stopajli * (oranlar.kdv / 100);
  const kdvli = stopajli + kdv;

  const hakedis = harcama * (oranlar.hakedis / 100);

  return { harcama, ucret, ucretli: ucretTanimli ? ucretli : null, stopaj, stopajli, kdv, kdvli, hakedis };
}

const BOS_TOPLAM = () => ({
  harcama: 0, ucret: 0, ucretli: 0, stopaj: 0, stopajli: 0, kdv: 0, kdvli: 0, hakedis: 0,
});

function topla(hedef, satir) {
  hedef.harcama += satir.harcama;
  hedef.ucret += satir.ucret;
  hedef.ucretli += satir.ucretli ?? satir.harcama;
  hedef.stopaj += satir.stopaj;
  hedef.stopajli += satir.stopajli;
  hedef.kdv += satir.kdv;
  hedef.kdvli += satir.kdvli;
  hedef.hakedis += satir.hakedis;
  return hedef;
}

/**
 * Bir ayın tüm markaları için fatura tablosu.
 * Marka sırası backend'in verdiği sıradır (grup tanımındaki sıra).
 */
function faturaKur(veri, ay, oranlar) {
  const markalar = (veri?.brands || []).map((b) => {
    const m = b.months?.[ay] || {};
    const platformlar = m.platforms || {};
    const hataliMecralar = new Set((b.errors || []).map((e) => e.platform));
    const bagliMecralar = new Set((b.accounts || []).map((a) => a.platform));

    const satirlar = MECRALAR.map((mecra) => {
      const harcama = Number(platformlar[mecra.key]?.spend || 0);
      return {
        mecra,
        bagli: bagliMecralar.has(mecra.key),
        hata: hataliMecralar.has(mecra.key),
        ...satirHesabi(harcama, mecra, ay, oranlar),
      };
    });

    return {
      id: b.id,
      label: b.label,
      logo: b.logo,
      accent: b.accent,
      satirlar,
      toplam: satirlar.reduce((t, s) => topla(t, s), BOS_TOPLAM()),
    };
  });

  const grup = markalar.reduce(
    (t, m) => {
      t.harcama += m.toplam.harcama;
      t.ucret += m.toplam.ucret;
      t.ucretli += m.toplam.ucretli;
      t.stopaj += m.toplam.stopaj;
      t.stopajli += m.toplam.stopajli;
      t.kdv += m.toplam.kdv;
      t.kdvli += m.toplam.kdvli;
      t.hakedis += m.toplam.hakedis;
      return t;
    },
    BOS_TOPLAM()
  );

  return { markalar, grup };
}

/* ── Küçük parçalar ────────────────────────────────────────────────── */

/** Sağa yaslı para hücresi. `bos` → ölçülmüyor/tanımsız, 0 DEĞİL. */
function P({ v, guclu = false, soluk = false, sinif = '' }) {
  const yok = v == null || !Number.isFinite(v);
  return (
    <td
      className={`whitespace-nowrap px-3 py-1.5 text-right tabular-nums ${
        guclu ? 'font-semibold text-slate-900' : soluk || yok ? 'text-slate-400' : 'text-slate-700'
      } ${sinif}`}
    >
      {yok ? '—' : sayi(v)}
    </td>
  );
}

function OranKutusu({ etiket, deger, onChange, ipucu }) {
  return (
    <label className="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-slate-200" title={ipucu}>
      <span className="text-[11px] font-medium text-slate-500">{etiket}</span>
      <span className="text-[11px] text-slate-400">%</span>
      <input
        type="number"
        step="0.05"
        min="0"
        max="99"
        value={deger}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-14 rounded border-0 bg-slate-50 px-1.5 py-0.5 text-right text-xs font-semibold tabular-nums text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-400"
      />
    </label>
  );
}

/* ── Excel ─────────────────────────────────────────────────────────── */

const XL_PARA = '#,##0.00';
const xlKenar = {
  top: { style: 'thin', color: { rgb: 'CBD5E1' } },
  bottom: { style: 'thin', color: { rgb: 'CBD5E1' } },
  left: { style: 'thin', color: { rgb: 'CBD5E1' } },
  right: { style: 'thin', color: { rgb: 'CBD5E1' } },
};
const XS = {
  baslik: {
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 10 },
    fill: { patternType: 'solid', fgColor: { rgb: '0F172A' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: xlKenar,
  },
  metin: { font: { sz: 10 }, alignment: { horizontal: 'left', vertical: 'center' }, border: xlKenar },
  marka: {
    font: { bold: true, sz: 10 },
    fill: { patternType: 'solid', fgColor: { rgb: 'F1F5F9' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: xlKenar,
  },
  sayi: { font: { sz: 10 }, alignment: { horizontal: 'right', vertical: 'center' }, border: xlKenar },
  vurgu: {
    font: { bold: true, sz: 10 },
    fill: { patternType: 'solid', fgColor: { rgb: 'FEF9C3' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: xlKenar,
  },
  toplam: {
    font: { bold: true, sz: 10 },
    fill: { patternType: 'solid', fgColor: { rgb: 'F1F5F9' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: xlKenar,
  },
  grup: {
    font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '0F172A' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: xlKenar,
  },
  not: { font: { sz: 9, color: { rgb: '475569' } }, alignment: { horizontal: 'left', vertical: 'top', wrapText: true } },
};
const xt = (v, s = XS.metin) => ({ v: v == null ? '' : String(v), t: 's', s });
const xn = (v, s = XS.sayi) =>
  v == null || !Number.isFinite(v) ? { v: '—', t: 's', s } : { v: Number(v), t: 'n', z: XL_PARA, s };

/* ── Sayfa ─────────────────────────────────────────────────────────── */

export default function HupalupaGroupBilling() {
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [seciliAy, setSeciliAy] = useState(null);
  const [oranlar, setOranlar] = useState(VARSAYILAN_ORANLAR);

  const getir = useCallback(async (yenile = false) => {
    setYukleniyor(true);
    setHata(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/group/hupalupa/monthly?period=ay&count=${AY_SAYISI}${yenile ? '&refresh=1' : ''}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          credentials: 'include',
        }
      );
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
      setVeri(json);
      setSeciliAy((onceki) =>
        onceki && json.months.includes(onceki) ? onceki : json.months[json.months.length - 1]
      );
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => { getir(false); }, [getir]);

  const fatura = useMemo(
    () => (veri && seciliAy ? faturaKur(veri, seciliAy, oranlar) : null),
    [veri, seciliAy, oranlar]
  );

  /* Alt tablo: her ayın grup toplamı — fatura tutarı ay ay nasıl gitmiş. */
  const aylikOzet = useMemo(() => {
    if (!veri) return [];
    return veri.months.map((ay) => ({ ay, ...faturaKur(veri, ay, oranlar).grup }));
  }, [veri, oranlar]);

  /* Kısmi ay uyarısı: içinde bulunulan ay henüz kapanmadı, fatura tutarı
     büyümeye devam eder. Patron panelinde bu tuzak bir kez ısırdı. */
  const donemBilgisi = useMemo(
    () => (veri?.periods || []).find((d) => d.key === seciliAy) || null,
    [veri, seciliAy]
  );
  // 🔑 Backend zaten söylüyor (`devamEdiyor`); burada tarihten yeniden
  // türetmek saat dilimi farkında bir gün kayıyor.
  const kismiAy = !!donemBilgisi?.devamEdiyor;

  /* 🔶 GEÇİCİ: Keramet indirim simülasyonu (yukarıdaki hesaba karışmaz). */
  const [simIndirim, setSimIndirim] = useState(SIM_VARSAYILAN_INDIRIM);
  const simulasyon = useMemo(() => {
    const marka = (fatura?.markalar || []).find((m) => SIM_MARKA_DESENI.test(m.label || ''));
    if (!marka) return null;
    const carpan = Math.max(0, 1 - simIndirim / 100);
    const satirlar = marka.satirlar.map((s) => ({
      mecra: s.mecra,
      mevcut: s.harcama,
      ...satirHesabi(s.harcama * carpan, s.mecra, seciliAy, oranlar),
    }));
    return {
      marka,
      carpan,
      satirlar,
      toplam: satirlar.reduce((t, s) => topla(t, s), BOS_TOPLAM()),
    };
  }, [fatura, simIndirim, seciliAy, oranlar]);

  const tumHatalar = useMemo(
    () => (veri?.brands || []).flatMap((b) => (b.errors || []).map((e) => ({ marka: b.label, ...e }))),
    [veri]
  );
  /* 🔴 TL dışı hesap fatura tablosunu sessizce bozar — çevrim YAPILMIYOR. */
  const yabanciParaBirimi = useMemo(
    () =>
      (veri?.brands || [])
        .flatMap((b) => (b.accounts || []).map((a) => ({ marka: b.label, ...a })))
        .filter((a) => a.currency && !['TRY', 'TL'].includes(String(a.currency).toUpperCase())),
    [veri]
  );

  /**
   * Excel çıktısı — hücreler SABİT DEĞİL, canlı FORMÜL.
   *
   * 🔑🔑 Oranlar sayfanın başında kendi hücrelerinde durur (`$B$4`…`$B$8`) ve
   * bütün merdiven onlara bağlıdır: muhasebe stopajı %15'ten %20'ye çekince
   * tablo kendi kendine yeniden hesaplar. Tutarı elle yazsaydık dosya
   * indirildiği andan itibaren ölü bir fotoğraf olurdu.
   *
   * 🔑 Her hücre hem `f` (formül) hem `v` (hesaplanmış değer) taşır: formülü
   * çalıştırmayan görüntüleyiciler (önizleme, Google Drive'ın hızlı bakışı)
   * yine de doğru rakamı gösterir.
   *
   * 🔴 Ücret sütunu ücretsiz mecralarda (TikTok, Temmuz 2026 öncesi Meta) boş
   * BIRAKILMAZ, `=C{n}` yazılır — ekranda "—" görünen yerde Excel'de zincirin
   * kopmaması için. Boş bıraksaydık altındaki stopaj formülü sıfıra düşerdi.
   */
  const excelIndir = useCallback(() => {
    if (!fatura || !seciliAy) return;

    /* Formül + hesaplanmış değer taşıyan hücre. */
    const xf = (formul, deger, stil = XS.sayi) => ({
      t: 'n', f: formul, v: Number.isFinite(deger) ? deger : 0, z: XL_PARA, s: stil,
    });
    const yuzde = (v, stil = XS.sayi) => ({ t: 'n', v, z: '0.00%', s: stil });

    const sayfaAdi = ayEtiketi(seciliAy, true);
    /* Sayfa adında boşluk var → başka sayfadan atıfta tırnak ŞART. */
    const ref = (adres) => `'${sayfaAdi}'!${adres}`;

    /* ── Oran bloğu (satır 4-8; A sütunu etiket, B sütunu değer) ── */
    const O = { google: 'B4', meta: 'B5', stopaj: 'B6', kdv: 'B7', hakedis: 'B8' };
    const M = (a) => `$${a[0]}$${a.slice(1)}`; // B4 → $B$4

    /* 🔑 Marka toplamı İKİ sütun: I = KDV hariç (stopaj dahil matrah, kesilecek
       faturanın tutarı), J = KDV dahil. Sütun harfleri sayfa 2 ve 3'ten atıfla
       kullanılıyor — sıra değişirse orayı da güncelle. */
    const B = [
      'Marka', 'Mecra', 'Harcama', 'Mecra Ücreti Dahil',
      'Stopaj', 'Stopaj Dahil', 'KDV', 'KDV Dahil',
      'Marka Toplamı (KDV Hariç)', 'Marka Toplamı (KDV Dahil)',
      'Glowy Hakediş', 'Hakediş Toplamı',
    ];

    const satirlar = [
      [xt(`HUPALUPA Grubu · Faturalandırma · ${ayEtiketi(seciliAy)}`, XS.marka)],
      [xt('Aşağıdaki oranları değiştirin, tablo kendini yeniden hesaplar.', XS.not)],
      [],
      [xt('Google ücreti', XS.marka), yuzde(oranlar.google / 100, XS.vurgu)],
      [xt('Meta ücreti', XS.marka), yuzde(oranlar.meta / 100, XS.vurgu)],
      [xt('Stopaj', XS.marka), yuzde(oranlar.stopaj / 100, XS.vurgu)],
      [xt('KDV', XS.marka), yuzde(oranlar.kdv / 100, XS.vurgu)],
      [xt('Glowy hakedişi', XS.marka), yuzde(oranlar.hakedis / 100, XS.vurgu)],
      [xt('Meta ücreti başlangıcı', XS.marka), xt(META_FEE_BASLANGIC, XS.metin)],
      [],
      B.map((h) => xt(h, XS.baslik)),
    ];

    const merges = [];
    const ilkVeriSatiri = satirlar.length; // 0 tabanlı
    for (const m of fatura.markalar) {
      const ilk = satirlar.length;
      m.satirlar.forEach((sat, i) => {
        const r = satirlar.length + 1; // Excel 1 tabanlı
        /* Ücret oranı hücresi: Google her ay, Meta yalnız başlangıçtan sonra.
           Ücretsiz satırda formül `=C{r}` — zincir kopmasın. */
        const oranHucre =
          sat.mecra.ucret === 'google'
            ? M(O.google)
            : sat.mecra.ucret === 'meta' && seciliAy >= META_FEE_BASLANGIC
            ? M(O.meta)
            : null;

        satirlar.push([
          i === 0 ? xt(m.label, XS.marka) : xt('', XS.marka),
          xt(sat.mecra.label),
          // Harcama TEK ham veri — merdivenin tamamı buna bağlı.
          { t: 'n', v: sat.harcama, z: XL_PARA, s: XS.vurgu },
          xf(oranHucre ? `C${r}*(1+${oranHucre})` : `C${r}`, sat.ucretli ?? sat.harcama),
          xf(`D${r}/(1-${M(O.stopaj)})-D${r}`, sat.stopaj),
          xf(`D${r}+E${r}`, sat.stopajli),
          xf(`F${r}*${M(O.kdv)}`, sat.kdv),
          xf(`F${r}+G${r}`, sat.kdvli, XS.toplam),
          null, // marka toplamı (KDV hariç) — birleştirilmiş hücreye yazılır
          null, // marka toplamı (KDV dahil) — birleştirilmiş hücreye yazılır
          xf(`C${r}*${M(O.hakedis)}`, sat.hakedis, XS.vurgu),
          null,
        ]);
      });
      const son = satirlar.length - 1;
      const ilkE = ilk + 1;
      const sonE = son + 1;
      satirlar[ilk][8] = xf(`SUM(F${ilkE}:F${sonE})`, m.toplam.stopajli, XS.toplam);
      satirlar[ilk][9] = xf(`SUM(H${ilkE}:H${sonE})`, m.toplam.kdvli, XS.toplam);
      satirlar[ilk][11] = xf(`SUM(K${ilkE}:K${sonE})`, m.toplam.hakedis, XS.toplam);
      for (let r = ilk + 1; r <= son; r++) {
        satirlar[r][8] = xt('', XS.toplam);
        satirlar[r][9] = xt('', XS.toplam);
        satirlar[r][11] = xt('', XS.toplam);
      }
      merges.push({ s: { r: ilk, c: 0 }, e: { r: son, c: 0 } });
      merges.push({ s: { r: ilk, c: 8 }, e: { r: son, c: 8 } });
      merges.push({ s: { r: ilk, c: 9 }, e: { r: son, c: 9 } });
      merges.push({ s: { r: ilk, c: 11 }, e: { r: son, c: 11 } });
    }

    const g = fatura.grup;
    const vBas = ilkVeriSatiri + 1;                 // ilk veri satırı, Excel 1 tabanlı
    const vSon = satirlar.length;                   // son veri satırı
    /* 🔑 I, J ve L birleştirilmiş sütunlar: yalnız marka başlarında sayı var,
       aradaki hücreler metin — `SUM` metni yok sayar, çift toplama olmaz. */
    satirlar.push([
      xt('GRUP TOPLAMI', XS.grup), xt('', XS.grup),
      ...['C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'].map((sut, i) =>
        xf(`SUM(${sut}${vBas}:${sut}${vSon})`,
          [g.harcama, g.ucretli, g.stopaj, g.stopajli, g.kdv, g.kdvli,
           g.stopajli, g.kdvli, g.hakedis, g.hakedis][i],
          XS.grup)
      ),
    ]);
    const grupSatiri = satirlar.length; // 1 tabanlı: GRUP TOPLAMI satırı

    /* 🔴 Sayfanın altındaki AÇIKLAMA BLOĞU bilerek kaldırıldı (Efe, 1 Eyl 2026:
       "şu satırları kaldır") — merdiven, brütleştirme, hakediş matrahı ve Meta
       tarih kuralını anlatan maddeler. Geri koyma; formüller zaten hücrelerde
       görünüyor. Kalan tek satır kısmi ay UYARISI: o bir açıklama değil,
       "bu rakam henüz kesin değil" bayrağı ve yalnız ay kapanmamışsa yazılır. */
    if (kismiAy) {
      satirlar.push([]);
      satirlar.push([xt('DİKKAT: seçili ay henüz kapanmadı, tutarlar ay sonuna kadar artacak.', XS.not)]);
    }

    const ws = {};
    let maxC = B.length - 1;
    satirlar.forEach((satir, r) => {
      (satir || []).forEach((h, c) => {
        if (h == null) return;
        ws[XLSX.utils.encode_cell({ r, c })] = h;
        if (c > maxC) maxC = c;
      });
    });
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: satirlar.length - 1, c: maxC } });
    ws['!cols'] = [{ wch: 22 }, { wch: 13 }, ...Array.from({ length: 10 }, () => ({ wch: 16 }))];
    ws['!merges'] = merges;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sayfaAdi);

    /* ── İkinci sayfa: ay ay grup toplamı ──────────────────────────────
       🔴 Burada formül YOK ve olamaz: diğer ayların mecra kırılımı bu dosyada
       hiç yok, atıf yapacak hücre bulunmuyor. Rakamlar panelde hesaplanmış
       hâlleriyle yazılır; seçili ayın satırı sayfa 1'e bağlanır. */
    const o = [
      [xt('Ay', XS.baslik), xt('Harcama', XS.baslik), xt('Mecra ücreti', XS.baslik),
       xt('Fatura Toplamı', XS.baslik), xt('Glowy Hakediş', XS.baslik)],
    ];
    aylikOzet.forEach((r) => {
      const bu = r.ay === seciliAy;
      o.push([
        xt(ayEtiketi(r.ay), bu ? XS.marka : XS.metin),
        bu ? xf(ref(`C${grupSatiri}`), r.harcama) : xn(r.harcama),
        bu ? xf(`${ref(`D${grupSatiri}`)}-${ref(`C${grupSatiri}`)}`, r.ucret) : xn(r.ucret),
        bu ? xf(ref(`H${grupSatiri}`), r.kdvli) : xn(r.kdvli),
        bu ? xf(ref(`K${grupSatiri}`), r.hakedis) : xn(r.hakedis),
      ]);
    });
    const ws2 = {};
    o.forEach((satir, r) => satir.forEach((h, c) => { ws2[XLSX.utils.encode_cell({ r, c })] = h; }));
    ws2['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: o.length - 1, c: 4 } });
    ws2['!cols'] = Array.from({ length: 5 }, () => ({ wch: 16 }));
    XLSX.utils.book_append_sheet(wb, ws2, 'Aylık özet');

    /* ── Üçüncü sayfa: marka marka toplam (SEÇİLİ AY) ──────────────────
       Tamamı sayfa 1'e atıf — orada oran değişince burası da döner. */
    const mb = [
      'Marka', ...MECRALAR.map((x) => x.label), 'Mecra harcaması',
      'Fatura toplamı (KDV hariç)', 'Fatura toplamı (KDV dahil)', 'Glowy hakedişi',
    ];
    const mo = [
      [xt(`Marka marka toplam · ${ayEtiketi(seciliAy)}`, XS.marka)],
      [xt(`Bütün hücreler '${sayfaAdi}' sayfasına bağlıdır.`, XS.not)],
      [],
      mb.map((h) => xt(h, XS.baslik)),
    ];
    fatura.markalar.forEach((m, mi) => {
      const r = mo.length + 1;                       // bu satırın Excel numarası
      const kaynak = ilkVeriSatiri + mi * MECRALAR.length + 1; // sayfa 1'deki ilk mecra satırı
      mo.push([
        xt(m.label, XS.marka),
        ...m.satirlar.map((sat, i) => xf(ref(`C${kaynak + i}`), sat.harcama)),
        xf(`SUM(B${r}:D${r})`, m.toplam.harcama, XS.vurgu),
        xf(ref(`I${kaynak}`), m.toplam.stopajli),
        xf(ref(`J${kaynak}`), m.toplam.kdvli),
        xf(ref(`L${kaynak}`), m.toplam.hakedis, XS.vurgu),
      ]);
    });
    const gBas = 5;                                  // ilk marka satırı (1 tabanlı)
    const gSon = mo.length;                          // son marka satırı
    mo.push([
      xt('GRUP', XS.grup),
      ...['B', 'C', 'D', 'E', 'F', 'G', 'H'].map((sut, i) =>
        xf(`SUM(${sut}${gBas}:${sut}${gSon})`,
          [
            ...MECRALAR.map((x) =>
              fatura.markalar.reduce(
                (t, m) => t + (m.satirlar.find((sat) => sat.mecra.key === x.key)?.harcama || 0), 0)),
            g.harcama, g.stopajli, g.kdvli, g.hakedis,
          ][i],
          XS.grup)
      ),
    ]);
    const ws3 = {};
    mo.forEach((satir, r) => (satir || []).forEach((h, c) => { ws3[XLSX.utils.encode_cell({ r, c })] = h; }));
    ws3['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: mo.length - 1, c: mb.length - 1 } });
    ws3['!cols'] = [{ wch: 22 }, ...Array.from({ length: mb.length - 1 }, () => ({ wch: 16 }))];
    XLSX.utils.book_append_sheet(wb, ws3, 'Marka toplamı');

    /* 🔑 `cellFormula` olmadan formüller yazılmaz; Excel dosyayı açar açmaz
       yeniden hesaplasın diye `calcChain` da temizlenmiş sayılır. */
    XLSX.writeFile(wb, `HUPALUPA_Faturalandirma_${seciliAy}.xlsx`, { cellFormula: true, bookSST: false });
  }, [fatura, seciliAy, oranlar, aylikOzet, kismiAy]);

  /* ── Durumlar ── */

  if (yukleniyor && !veri) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Fatura verisi canlı çekiliyor…
      </div>
    );
  }

  if (hata && !veri) {
    return (
      <div className="mx-auto mt-10 max-w-lg rounded-xl bg-rose-50 p-5 ring-1 ring-rose-200">
        <div className="flex items-center gap-2 font-semibold text-rose-900">
          <AlertTriangle className="h-4 w-4" /> Sayfa yüklenemedi
        </div>
        <p className="mt-1 text-sm text-rose-800">{hata}</p>
        <button
          onClick={() => getir(true)}
          className="mt-3 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700"
        >
          Tekrar dene
        </button>
      </div>
    );
  }

  if (!veri || !seciliAy || !fatura) return null;

  const g = fatura.grup;

  return (
    /* lang="tr" ŞART: başlıklarda `uppercase` var, tarayıcı Türkçe kuralını
       bilmezse "Toplam" → "TOPLAM" derken noktasız İ basıyor. */
    <div lang="tr" className="mx-auto max-w-[1500px] px-4 py-5 print:max-w-none print:px-0">
      {/* ── Başlık ── */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link
            to="/grup/hupalupa"
            className="mb-1 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-900 print:hidden"
          >
            <ArrowLeft className="h-3 w-3" /> Patron paneline dön
          </Link>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-slate-900">HUPALUPA Grubu · Faturalandırma</h1>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              canlı
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            Dört marka · harcama patron paneliyle aynı uçtan geliyor ·{' '}
            {veri.cached ? 'önbellekten' : `${(veri.elapsedMs / 1000).toFixed(1)} sn'de çekildi`}
            {donemBilgisi?.since ? ` · ${donemBilgisi.since} → ${donemBilgisi.until}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-2 print:hidden">
          <div className="flex overflow-hidden rounded-lg ring-1 ring-slate-200">
            {veri.months.map((m) => (
              <button
                key={m}
                onClick={() => setSeciliAy(m)}
                title={ayEtiketi(m)}
                className={`px-2.5 py-1.5 text-xs font-medium transition ${
                  m === seciliAy ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {ayEtiketi(m, true)}
              </button>
            ))}
          </div>
          <button
            onClick={() => getir(true)}
            disabled={yukleniyor}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${yukleniyor ? 'animate-spin' : ''}`} /> Yenile
          </button>
          <button
            onClick={excelIndir}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
          >
            <Printer className="h-3.5 w-3.5" /> Yazdır
          </button>
        </div>
      </div>

      {/* ── Oranlar ── */}
      <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl bg-slate-50 p-2.5 ring-1 ring-slate-200 print:hidden">
        <span className="pl-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Oranlar</span>
        <OranKutusu etiket="Google ücreti" deger={oranlar.google} ipucu="Türkiye dijital hizmet vergisi — Google faturasında tutarın içinde"
          onChange={(v) => setOranlar((o) => ({ ...o, google: v }))} />
        <OranKutusu etiket="Meta ücreti" deger={oranlar.meta} ipucu={`Konum ücreti — ${ayEtiketi(META_FEE_BASLANGIC)} ayından itibaren uygulanır`}
          onChange={(v) => setOranlar((o) => ({ ...o, meta: v }))} />
        <OranKutusu etiket="Stopaj" deger={oranlar.stopaj} ipucu="Brütleştirilir: tutar ÷ (1 − oran) − tutar"
          onChange={(v) => setOranlar((o) => ({ ...o, stopaj: v }))} />
        <OranKutusu etiket="KDV" deger={oranlar.kdv} onChange={(v) => setOranlar((o) => ({ ...o, kdv: v }))} />
        <OranKutusu etiket="Glowy hakedişi" deger={oranlar.hakedis} ipucu="Ham harcama üzerinden, yükler bindirilmeden önce"
          onChange={(v) => setOranlar((o) => ({ ...o, hakedis: v }))} />
        {JSON.stringify(oranlar) !== JSON.stringify(VARSAYILAN_ORANLAR) && (
          <button
            onClick={() => setOranlar(VARSAYILAN_ORANLAR)}
            className="ml-auto rounded-lg px-2.5 py-1.5 text-[11px] font-medium text-slate-500 hover:bg-white hover:text-slate-900"
          >
            Varsayılana dön
          </button>
        )}
      </div>

      {/* ── Uyarılar ── */}
      {kismiAy && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 ring-1 ring-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <b>{ayEtiketi(seciliAy)}</b> henüz kapanmadı
            {donemBilgisi?.gecenGun && donemBilgisi?.toplamGun
              ? ` (${donemBilgisi.gecenGun}/${donemBilgisi.toplamGun} gün)`
              : ''}
            . Tutarlar ay sonuna kadar artmaya devam eder — fatura kontrolünü kapanmış ayda yapın.
          </span>
        </div>
      )}
      {yabanciParaBirimi.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900 ring-1 ring-rose-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            TL dışı para birimli hesap var, <b>çevrim yapılmıyor</b>:{' '}
            {yabanciParaBirimi.map((a) => `${a.marka} · ${a.name} (${a.currency})`).join(' · ')}
          </span>
        </div>
      )}
      {tumHatalar.length > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-900 ring-1 ring-rose-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {tumHatalar.length} hesap okunamadı — o mecranın harcaması <b>eksik</b> olabilir:{' '}
            {tumHatalar.map((e) => `${e.marka} · ${e.account}`).join(' · ')}
          </span>
        </div>
      )}

      {/* ── Fatura tablosu ── */}
      <div className="mt-4 overflow-x-auto rounded-xl bg-white ring-1 ring-slate-200">
        <table className="w-full min-w-[1280px] border-collapse text-xs">
          <thead>
            <tr className="bg-slate-900 text-white">
              <th className="px-3 py-2 text-left font-semibold">Marka</th>
              <th className="px-3 py-2 text-left font-semibold">Mecra</th>
              <th className="px-3 py-2 text-right font-semibold">Harcama</th>
              <th className="px-3 py-2 text-right font-semibold">
                Mecra Ücreti Dahil
                <div className="text-[10px] font-normal text-slate-300">
                  Google %{sayi(oranlar.google, 1)} · Meta %{sayi(oranlar.meta, 1)}
                </div>
              </th>
              <th className="px-3 py-2 text-right font-semibold">Stopaj %{sayi(oranlar.stopaj, 0)}</th>
              <th className="px-3 py-2 text-right font-semibold">Stopaj Dahil</th>
              <th className="px-3 py-2 text-right font-semibold">KDV %{sayi(oranlar.kdv, 0)}</th>
              <th className="px-3 py-2 text-right font-semibold">KDV Dahil</th>
              <th className="border-l border-slate-700 px-3 py-2 text-right font-semibold">
                Marka Toplamı
                <div className="text-[10px] font-normal text-slate-300">KDV hariç</div>
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                Marka Toplamı
                <div className="text-[10px] font-normal text-slate-300">KDV dahil</div>
              </th>
              <th className="border-l border-slate-700 px-3 py-2 text-right font-semibold">
                Glowy Hakediş
                <div className="text-[10px] font-normal text-slate-300">harcamanın %{sayi(oranlar.hakedis, 0)}'u</div>
              </th>
              <th className="px-3 py-2 text-right font-semibold">Hakediş Toplamı</th>
            </tr>
          </thead>

          {fatura.markalar.map((m) => (
            <tbody key={m.id} className="border-t-2 border-slate-200">
              {m.satirlar.map((s, i) => (
                <tr key={s.mecra.key} className="border-t border-slate-100 hover:bg-slate-50/60">
                  {i === 0 && (
                    <td
                      rowSpan={m.satirlar.length}
                      className="border-r border-slate-100 bg-slate-50/70 px-3 py-2 align-middle"
                    >
                      <div className="flex items-center gap-2">
                        {m.logo && (
                          <img
                            src={m.logo}
                            alt=""
                            className="h-6 w-6 shrink-0 rounded object-contain"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                        <span className="font-semibold text-slate-900">{m.label}</span>
                      </div>
                    </td>
                  )}

                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span className="inline-flex items-center gap-1.5">
                      <img
                        src={s.mecra.logo}
                        alt=""
                        className="h-3.5 w-3.5 object-contain"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                      <span className={s.bagli ? 'text-slate-700' : 'text-slate-400'}>{s.mecra.label}</span>
                      {s.hata && (
                        <span
                          title="Bu mecrada okunamayan hesap var — harcama eksik olabilir"
                          className="rounded bg-rose-100 px-1 text-[10px] font-semibold text-rose-700"
                        >
                          !
                        </span>
                      )}
                      {!s.bagli && (
                        <span
                          title="Bu markada bu mecranın bağlı hesabı yok"
                          className="text-[10px] text-slate-400"
                        >
                          bağlı değil
                        </span>
                      )}
                    </span>
                  </td>

                  {/* Harcama — Excel'de sarı boyanan sütun, faturanın matrahı */}
                  <P v={s.harcama} guclu sinif="bg-amber-50/70" />
                  <P v={s.ucretli} />
                  <P v={s.stopaj} />
                  <P v={s.stopajli} />
                  <P v={s.kdv} />
                  <P v={s.kdvli} guclu />

                  {/* 🔑 İki marka toplamı (Efe, 2 Eyl 2026): KDV hariç =
                      kesilecek faturanın matrahı, KDV dahil = ödenecek tutar. */}
                  {i === 0 && (
                    <td
                      rowSpan={m.satirlar.length}
                      className="whitespace-nowrap border-l border-slate-200 bg-slate-50 px-3 py-2 text-right align-middle text-sm font-semibold tabular-nums text-slate-600"
                    >
                      {sayi(m.toplam.stopajli)}
                    </td>
                  )}

                  {i === 0 && (
                    <td
                      rowSpan={m.satirlar.length}
                      className="whitespace-nowrap bg-slate-50 px-3 py-2 text-right align-middle text-sm font-bold tabular-nums text-slate-900"
                    >
                      {sayi(m.toplam.kdvli)}
                    </td>
                  )}

                  <P v={s.hakedis} sinif="border-l border-slate-200 bg-amber-50/70 font-medium" />

                  {i === 0 && (
                    <td
                      rowSpan={m.satirlar.length}
                      className="whitespace-nowrap bg-slate-50 px-3 py-2 text-right align-middle text-sm font-bold tabular-nums text-slate-900"
                    >
                      {sayi(m.toplam.hakedis)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          ))}

          <tfoot>
            <tr className="bg-slate-900 text-white">
              <td className="px-3 py-2.5 font-bold uppercase tracking-wide" colSpan={2}>
                Grup Toplamı
              </td>
              <td className="px-3 py-2.5 text-right font-bold tabular-nums">{sayi(g.harcama)}</td>
              <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-200">{sayi(g.ucretli)}</td>
              <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-200">{sayi(g.stopaj)}</td>
              <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-200">{sayi(g.stopajli)}</td>
              <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-200">{sayi(g.kdv)}</td>
              <td className="px-3 py-2.5 text-right font-bold tabular-nums">{sayi(g.kdvli)}</td>
              <td className="border-l border-slate-700 px-3 py-2.5 text-right text-sm font-semibold tabular-nums text-slate-200">
                {sayi(g.stopajli)}
              </td>
              <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums">
                {sayi(g.kdvli)}
              </td>
              <td className="border-l border-slate-700 px-3 py-2.5 text-right font-bold tabular-nums">
                {sayi(g.hakedis)}
              </td>
              <td className="px-3 py-2.5 text-right text-sm font-bold tabular-nums">{sayi(g.hakedis)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ── Özet kartlar ── */}
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { ad: 'Mecra harcaması', v: g.harcama, alt: 'ham, yüklersiz' },
          { ad: 'Mecra ücreti', v: g.ucret, alt: `Google %${sayi(oranlar.google, 1)} + Meta %${sayi(oranlar.meta, 1)}` },
          { ad: 'Fatura toplamı', v: g.kdvli, alt: `KDV hariç ₺${sayi(g.stopajli)}`, vurgu: true },
          { ad: 'Glowy hakedişi', v: g.hakedis, alt: `harcamanın %${sayi(oranlar.hakedis, 0)}'u`, vurgu: true },
        ].map((k) => (
          <div
            key={k.ad}
            className={`rounded-xl p-3.5 ring-1 ${
              k.vurgu ? 'bg-slate-900 ring-slate-900' : 'bg-white ring-slate-200'
            }`}
          >
            <div className={`text-[11px] font-medium ${k.vurgu ? 'text-slate-300' : 'text-slate-500'}`}>{k.ad}</div>
            <div className={`mt-0.5 text-lg font-bold tabular-nums ${k.vurgu ? 'text-white' : 'text-slate-900'}`}>
              {para(k.v)}
            </div>
            <div className={`text-[11px] ${k.vurgu ? 'text-slate-400' : 'text-slate-400'}`}>{k.alt}</div>
          </div>
        ))}
      </div>

      {/* ── Marka marka toplam ─────────────────────────────────────────
          "HUPALUPA Store bu ay mecraya toplam ne harcadı, hakedişi ne oldu"
          sorusunun tek bakışta cevabı. 🔑 SEÇİLİ AYA aittir — üstteki büyük
          ızgarayla aynı ay; ay düğmeleri bu tabloyu da değiştirir. */}
      <div className="mt-5 overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Marka marka toplam
          </span>
          <span className="text-[11px] text-slate-400">{ayEtiketi(seciliAy)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                <th className="px-3 py-2 text-left font-semibold">Marka</th>
                {MECRALAR.map((x) => (
                  <th key={x.key} className="px-3 py-2 text-right font-medium text-slate-400">
                    {x.label}
                  </th>
                ))}
                <th className="border-l border-slate-200 px-3 py-2 text-right font-semibold">
                  Mecra harcaması
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  Fatura toplamı
                  <div className="text-[10px] font-normal text-slate-400">KDV hariç</div>
                </th>
                <th className="px-3 py-2 text-right font-semibold">
                  Fatura toplamı
                  <div className="text-[10px] font-normal text-slate-400">KDV dahil</div>
                </th>
                <th className="border-l border-slate-200 px-3 py-2 text-right font-semibold">
                  Glowy hakedişi
                </th>
              </tr>
            </thead>
            <tbody>
              {fatura.markalar.map((m) => (
                <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="whitespace-nowrap px-3 py-2">
                    <span className="inline-flex items-center gap-2">
                      {m.logo && (
                        <img
                          src={m.logo}
                          alt=""
                          className="h-5 w-5 shrink-0 rounded object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <span className="font-semibold text-slate-900">{m.label}</span>
                    </span>
                  </td>
                  {m.satirlar.map((sat) => (
                    <td
                      key={sat.mecra.key}
                      className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-500"
                    >
                      {sat.harcama ? sayi(sat.harcama) : '—'}
                    </td>
                  ))}
                  <td className="whitespace-nowrap border-l border-slate-200 bg-amber-50/60 px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-900">
                    {sayi(m.toplam.harcama)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums text-slate-500">
                    {sayi(m.toplam.stopajli)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                    {sayi(m.toplam.kdvli)}
                  </td>
                  <td className="whitespace-nowrap border-l border-slate-200 bg-amber-50/60 px-3 py-2 text-right text-sm font-bold tabular-nums text-slate-900">
                    {sayi(m.toplam.hakedis)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-900 text-white">
                <td className="px-3 py-2.5 font-bold uppercase tracking-wide">Grup</td>
                {MECRALAR.map((x) => (
                  <td key={x.key} className="px-3 py-2.5 text-right tabular-nums text-slate-300">
                    {sayi(fatura.markalar.reduce(
                      (t, m) => t + (m.satirlar.find((sat) => sat.mecra.key === x.key)?.harcama || 0),
                      0
                    ))}
                  </td>
                ))}
                <td className="border-l border-slate-700 px-3 py-2.5 text-right text-sm font-bold tabular-nums">
                  {sayi(g.harcama)}
                </td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-slate-300">
                  {sayi(g.stopajli)}
                </td>
                <td className="px-3 py-2.5 text-right font-bold tabular-nums">{sayi(g.kdvli)}</td>
                <td className="border-l border-slate-700 px-3 py-2.5 text-right text-sm font-bold tabular-nums">
                  {sayi(g.hakedis)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Aylık seyir ── */}
      <div className="mt-5 overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        <div className="border-b border-slate-100 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Ay ay grup toplamı
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-600">
                <th className="px-3 py-2 text-left font-semibold">Ay</th>
                <th className="px-3 py-2 text-right font-semibold">Harcama</th>
                <th className="px-3 py-2 text-right font-semibold">Mecra ücreti</th>
                <th className="px-3 py-2 text-right font-semibold">Fatura toplamı</th>
                <th className="px-3 py-2 text-right font-semibold">Glowy hakedişi</th>
              </tr>
            </thead>
            <tbody>
              {aylikOzet.map((r) => (
                <tr
                  key={r.ay}
                  onClick={() => setSeciliAy(r.ay)}
                  className={`cursor-pointer border-t border-slate-100 hover:bg-slate-50 ${
                    r.ay === seciliAy ? 'bg-slate-50 font-semibold' : ''
                  }`}
                >
                  <td className="px-3 py-1.5 text-slate-700">{ayEtiketi(r.ay)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{sayi(r.harcama)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{sayi(r.ucret)}</td>
                  <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-slate-900">{sayi(r.kdvli)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{sayi(r.hakedis)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── 🔶 GEÇİCİ · Keramet indirim simülasyonu ─────────────────────
          Efe, 2 Eyl 2026: bu ay Keramet Ilıcası'na %50 daha az fatura
          kesilecek. 🔴🔴 Yalnızca BU blok simüledir — yukarıdaki tablolar,
          özet kartlar ve Excel çıktısı canlı harcamayı gösterir. İş bitince
          blok komple silinecek. */}
      {simulasyon && (
        <div className="mt-5 overflow-hidden rounded-xl bg-amber-50/60 ring-2 ring-dashed ring-amber-300">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 px-4 py-2.5">
            <div>
              <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                Geçici · simülasyon
              </span>
              <span className="ml-2 text-xs font-semibold uppercase tracking-wide text-amber-900">
                {simulasyon.marka.label} — %{sayi(simIndirim, 0)} daha az fatura
              </span>
              <div className="mt-0.5 text-[11px] text-amber-800">
                {ayEtiketi(seciliAy)} · harcama %{sayi(simIndirim, 0)} azalmış gibi hesaplandı.
                Yukarıdaki tablolar ve Excel <b>değişmedi</b>, orada gerçek harcama var.
              </div>
            </div>
            <label className="flex items-center gap-1.5 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-amber-300">
              <span className="text-[11px] font-medium text-amber-800">İndirim</span>
              <span className="text-[11px] text-amber-500">%</span>
              <input
                type="number"
                step="5"
                min="0"
                max="100"
                value={simIndirim}
                onChange={(e) => setSimIndirim(Number(e.target.value))}
                className="w-14 rounded border-0 bg-amber-50 px-1.5 py-0.5 text-right text-xs font-semibold tabular-nums text-amber-900 focus:outline-none focus:ring-1 focus:ring-amber-400"
              />
            </label>
          </div>

          <div className="overflow-x-auto bg-white">
            <table className="w-full min-w-[900px] border-collapse text-xs">
              <thead>
                <tr className="bg-amber-100/70 text-amber-900">
                  <th className="px-3 py-2 text-left font-semibold">Mecra</th>
                  <th className="px-3 py-2 text-right font-medium text-amber-700">Gerçek harcama</th>
                  <th className="border-l border-amber-200 px-3 py-2 text-right font-semibold">
                    Simüle harcama
                    <div className="text-[10px] font-normal text-amber-700">
                      × {sayi(simulasyon.carpan, 2)}
                    </div>
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">Mecra Ücreti Dahil</th>
                  <th className="px-3 py-2 text-right font-semibold">Stopaj %{sayi(oranlar.stopaj, 0)}</th>
                  <th className="px-3 py-2 text-right font-semibold">Stopaj Dahil</th>
                  <th className="px-3 py-2 text-right font-semibold">KDV %{sayi(oranlar.kdv, 0)}</th>
                  <th className="px-3 py-2 text-right font-semibold">KDV Dahil</th>
                </tr>
              </thead>
              <tbody>
                {simulasyon.satirlar.map((s) => (
                  <tr key={s.mecra.key} className="border-t border-amber-100">
                    <td className="whitespace-nowrap px-3 py-1.5">
                      <span className="inline-flex items-center gap-1.5">
                        <img
                          src={s.mecra.logo}
                          alt=""
                          className="h-3.5 w-3.5 object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <span className="text-slate-700">{s.mecra.label}</span>
                      </span>
                    </td>
                    <P v={s.mevcut} soluk />
                    <P v={s.harcama} guclu sinif="border-l border-amber-200 bg-amber-50/70" />
                    <P v={s.ucretli} />
                    <P v={s.stopaj} />
                    <P v={s.stopajli} />
                    <P v={s.kdv} />
                    <P v={s.kdvli} guclu />
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-amber-900 text-white">
                  <td className="px-3 py-2.5 font-bold uppercase tracking-wide">Toplam</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-amber-200">
                    {sayi(simulasyon.marka.toplam.harcama)}
                  </td>
                  <td className="border-l border-amber-700 px-3 py-2.5 text-right font-bold tabular-nums">
                    {sayi(simulasyon.toplam.harcama)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-amber-100">
                    {sayi(simulasyon.toplam.ucretli)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-amber-100">
                    {sayi(simulasyon.toplam.stopaj)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-amber-100">
                    {sayi(simulasyon.toplam.stopajli)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-amber-100">
                    {sayi(simulasyon.toplam.kdv)}
                  </td>
                  <td className="px-3 py-2.5 text-right font-bold tabular-nums">
                    {sayi(simulasyon.toplam.kdvli)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Kesilecek rakamlar — gerçeğiyle yan yana. */}
          <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-3">
            {[
              {
                ad: 'Kesilecek fatura · KDV hariç',
                v: simulasyon.toplam.stopajli,
                mevcut: simulasyon.marka.toplam.stopajli,
                vurgu: true,
              },
              {
                ad: 'Kesilecek fatura · KDV dahil',
                v: simulasyon.toplam.kdvli,
                mevcut: simulasyon.marka.toplam.kdvli,
                vurgu: true,
              },
              {
                ad: `Glowy hakedişi · %${sayi(oranlar.hakedis, 0)}`,
                v: simulasyon.toplam.hakedis,
                mevcut: simulasyon.marka.toplam.hakedis,
              },
            ].map((k) => (
              <div
                key={k.ad}
                className={`rounded-xl p-3.5 ring-1 ${
                  k.vurgu ? 'bg-amber-900 text-white ring-amber-900' : 'bg-white ring-amber-200'
                }`}
              >
                <div className={`text-[11px] font-medium ${k.vurgu ? 'text-amber-200' : 'text-slate-500'}`}>
                  {k.ad}
                </div>
                <div className="mt-0.5 text-lg font-bold tabular-nums">{para(k.v)}</div>
                <div className={`text-[11px] tabular-nums ${k.vurgu ? 'text-amber-300' : 'text-slate-400'}`}>
                  gerçeği {para(k.mevcut)} · {para(Math.abs(k.mevcut - k.v))} daha az
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Okuma notları ── */}
      <div className="mt-4 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <Info className="h-3.5 w-3.5" /> Nasıl hesaplanıyor
        </div>
        <ul className="mt-2 space-y-1.5 text-[11px] leading-relaxed text-slate-600">
          <li>
            <b>Merdiven:</b> harcama × (1 + mecra ücreti) → ÷ (1 − stopaj) → × (1 + KDV). Mecra ücreti
            stopaj matrahının <b>içindedir</b>.
          </li>
          <li>
            <b>Stopaj brütleştirmedir</b>, düz çarpım değil: tutar ÷ {sayi(1 - oranlar.stopaj / 100, 2)} − tutar.
            ×{sayi(1 + oranlar.stopaj / 100, 2)} yazmak kalem başına ~%{sayi(((1 / (1 - oranlar.stopaj / 100)) - (1 + oranlar.stopaj / 100)) * 100, 2)} eksik verir.
          </li>
          <li>
            <b>Glowy hakedişi ham harcama üzerinden</b> hesaplanır — vergi, stopaj ve mecra ücreti
            bindirilmeden önce.
          </li>
          <li>
            <b>Meta konum ücreti %{sayi(oranlar.meta, 1)}</b> yalnızca {ayEtiketi(META_FEE_BASLANGIC)} ve sonrası
            için uygulanır; öncesindeki aylarda Meta satırında ücret sütunu boştur. Google'ın %{sayi(oranlar.google, 1)}'i
            Türkiye dijital hizmet vergisidir ve her ayda geçerlidir. TikTok'a mecra ücreti binmez.
          </li>
          <li>
            Harcama rakamları Meta / Google Ads / TikTok API'lerinden <b>canlı</b> okunur (patron paneliyle
            aynı uç, aynı önbellek). Yüklerin <b>hiçbiri API'den gelmez</b>, hepsi bu sayfada bindirilir.
          </li>
          <li>Oranlar bu ekranda geçici olarak değiştirilebilir; <b>kaydedilmez</b>, sayfa yenilenince varsayılana döner.</li>
        </ul>
      </div>
    </div>
  );
}
